import { useState, useCallback, useEffect } from "react";
import { BooksHealthRepositoryImpl } from "../../data/repositories/BooksHealthRepositoryImpl";
import { AccountantStatusRepositoryImpl } from "../../data/repositories/AccountantStatusRepositoryImpl";
import { GetBooksHealthActionRequiredUseCase } from "../../domain/use-cases/GetBooksHealthActionRequiredUseCase";
import { GetBooksHealthMonthByMonthUseCase } from "../../domain/use-cases/GetBooksHealthMonthByMonthUseCase";
import { GetBooksHealthMonthlyModuleStatusUseCase } from "../../domain/use-cases/GetBooksHealthMonthlyModuleStatusUseCase";
import { GetBooksHealthOverviewUseCase } from "../../domain/use-cases/GetBooksHealthOverviewUseCase";
import { GetBooksHealthRecentActivityUseCase } from "../../domain/use-cases/GetBooksHealthRecentActivityUseCase";
import { GetAccountantStatusSummaryUseCase } from "../../domain/use-cases/GetAccountantStatusSummaryUseCase";
import { GetAccountantStatusMonthDetailBillsUseCase } from "../../domain/use-cases/GetAccountantStatusMonthDetailBillsUseCase";
import { GetAccountantStatusMonthDetailInvoicesUseCase } from "../../domain/use-cases/GetAccountantStatusMonthDetailInvoicesUseCase";
import { GetAccountantStatusMonthDetailTransactionsUseCase } from "../../domain/use-cases/GetAccountantStatusMonthDetailTransactionsUseCase";
import { GetAccountantStatusWorkHistoryUseCase } from "../../domain/use-cases/GetAccountantStatusWorkHistoryUseCase";
import { GetAccountantPerformanceInsightsUseCase } from "../../domain/use-cases/GetAccountantPerformanceInsightsUseCase";
import type {
  ActionRequiredItemEntity,
  BooksHealthOverviewEntity,
  BooksMonthStatusItemEntity,
  MonthlyModuleStatusEntity,
  RecentBooksActivityEntity,
} from "../../domain/entities/BooksHealthEntity";
import type {
  AccountantPerformanceInsightsEntity,
  AccountantStatusBillsDetailEntity,
  AccountantStatusInvoicesDetailEntity,
  AccountantStatusSummaryEntity,
  AccountantStatusTransactionsDetailEntity,
  AccountantStatusWorkHistoryEntity,
} from "../../domain/entities/AccountantStatusEntity";
import type { GetBooksHealthOverviewParams } from "../../domain/repositories/BooksHealthRepository";
import {
  getEffectiveMonth,
  getEffectiveYear,
} from "../../../../utils/effectiveDate";

const repository = new BooksHealthRepositoryImpl();
const accountantStatusRepository = new AccountantStatusRepositoryImpl();
const getBooksHealthOverviewUseCase = new GetBooksHealthOverviewUseCase(
  repository,
);
const getBooksHealthActionRequiredUseCase =
  new GetBooksHealthActionRequiredUseCase(repository);
const getBooksHealthMonthByMonthUseCase = new GetBooksHealthMonthByMonthUseCase(
  repository,
);
const getBooksHealthMonthlyModuleStatusUseCase =
  new GetBooksHealthMonthlyModuleStatusUseCase(repository);
const getBooksHealthRecentActivityUseCase =
  new GetBooksHealthRecentActivityUseCase(repository);
const getAccountantStatusSummaryUseCase = new GetAccountantStatusSummaryUseCase(
  accountantStatusRepository,
);
const getAccountantStatusMonthDetailBillsUseCase =
  new GetAccountantStatusMonthDetailBillsUseCase(accountantStatusRepository);
const getAccountantStatusMonthDetailInvoicesUseCase =
  new GetAccountantStatusMonthDetailInvoicesUseCase(accountantStatusRepository);
const getAccountantStatusMonthDetailTransactionsUseCase =
  new GetAccountantStatusMonthDetailTransactionsUseCase(
    accountantStatusRepository,
  );
const getAccountantStatusWorkHistoryUseCase =
  new GetAccountantStatusWorkHistoryUseCase(accountantStatusRepository);
const getAccountantPerformanceInsightsUseCase =
  new GetAccountantPerformanceInsightsUseCase(accountantStatusRepository);

const BILLS_LIMIT = 10;
const INVOICES_LIMIT = 10;
type AccountantPeriod = "this_month" | "last_3_months";

const MONTH_ABBREVIATIONS = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

function getMonthNumberFromAbbreviation(abbrev: string): number {
  const index = MONTH_ABBREVIATIONS.indexOf(abbrev);
  return index >= 0 ? index + 1 : getEffectiveMonth();
}

export function useBooksHealthPage(params?: GetBooksHealthOverviewParams) {
  const [data, setData] = useState<BooksHealthOverviewEntity | null>(null);
  const [loading, setLoading] = useState(true);
  const [actionRequiredLoading, setActionRequiredLoading] = useState(true);
  const [monthByMonthLoading, setMonthByMonthLoading] = useState(true);
  const [monthlyModuleStatusLoading, setMonthlyModuleStatusLoading] =
    useState(true);
  const [error, setError] = useState<string | null>(null);
  const [actionRequiredError, setActionRequiredError] = useState<string | null>(
    null,
  );
  const [monthByMonthError, setMonthByMonthError] = useState<string | null>(
    null,
  );
  const [monthlyModuleStatusError, setMonthlyModuleStatusError] = useState<
    string | null
  >(null);
  const [actionRequiredItems, setActionRequiredItems] = useState<
    ActionRequiredItemEntity[] | null
  >(null);
  const [monthByMonth, setMonthByMonth] = useState<
    BooksMonthStatusItemEntity[] | null
  >(null);
  const [monthlyModuleStatus, setMonthlyModuleStatus] = useState<
    MonthlyModuleStatusEntity[] | null
  >(null);
  const [recentActivityLoading, setRecentActivityLoading] = useState(true);
  const [recentActivityError, setRecentActivityError] = useState<string | null>(
    null,
  );
  const [recentActivity, setRecentActivity] = useState<
    RecentBooksActivityEntity[] | null
  >(null);
  const [selectedYear, setSelectedYear] = useState<number>(getEffectiveYear());
  const [selectedMonth, setSelectedMonth] =
    useState<number>(getEffectiveMonth());
  const [summaryPeriod, setSummaryPeriod] =
    useState<AccountantPeriod>("last_3_months");
  const [detailYear, setDetailYear] = useState<number>(getEffectiveYear());
  const [detailMonth, setDetailMonth] = useState<number>(getEffectiveMonth());
  const [detailPeriod, setDetailPeriod] =
    useState<AccountantPeriod>("last_3_months");
  const [billsOffset, setBillsOffset] = useState<number>(0);
  const [invoicesOffset, setInvoicesOffset] = useState<number>(0);
  const [accountantSummary, setAccountantSummary] =
    useState<AccountantStatusSummaryEntity | null>(null);
  const [billsDetail, setBillsDetail] =
    useState<AccountantStatusBillsDetailEntity | null>(null);
  const [invoicesDetail, setInvoicesDetail] =
    useState<AccountantStatusInvoicesDetailEntity | null>(null);
  const [transactionsDetail, setTransactionsDetail] =
    useState<AccountantStatusTransactionsDetailEntity | null>(null);
  const [workHistory, setWorkHistory] = useState<
    AccountantStatusWorkHistoryEntity[]
  >([]);
  const [accountantSummaryLoading, setAccountantSummaryLoading] =
    useState<boolean>(true);
  const [billsDetailLoading, setBillsDetailLoading] = useState<boolean>(true);
  const [invoicesDetailLoading, setInvoicesDetailLoading] =
    useState<boolean>(true);
  const [transactionsDetailLoading, setTransactionsDetailLoading] =
    useState<boolean>(true);
  const [workHistoryLoading, setWorkHistoryLoading] = useState<boolean>(true);
  const [accountantSummaryError, setAccountantSummaryError] = useState<
    string | null
  >(null);
  const [billsDetailError, setBillsDetailError] = useState<string | null>(null);
  const [invoicesDetailError, setInvoicesDetailError] = useState<string | null>(
    null,
  );
  const [transactionsDetailError, setTransactionsDetailError] = useState<
    string | null
  >(null);
  const [workHistoryError, setWorkHistoryError] = useState<string | null>(null);
  const [accountantPerformanceInsights, setAccountantPerformanceInsights] =
    useState<AccountantPerformanceInsightsEntity | null>(null);
  const [
    accountantPerformanceInsightsLoading,
    setAccountantPerformanceInsightsLoading,
  ] = useState<boolean>(true);
  const [
    accountantPerformanceInsightsError,
    setAccountantPerformanceInsightsError,
  ] = useState<string | null>(null);

  const fetchOverview = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const entity = await getBooksHealthOverviewUseCase.execute(params);
      setData({
        ...entity,
        ...(actionRequiredItems != null ? { actionRequiredItems } : undefined),
        ...(monthByMonth != null
          ? { monthByMonthStatus: monthByMonth }
          : undefined),
        ...(monthlyModuleStatus != null ? { monthlyModuleStatus } : undefined),
        ...(recentActivity != null ? { recentActivity } : undefined),
      });
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Failed to load books health",
      );
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [
    params?.month,
    params?.year,
    actionRequiredItems,
    monthByMonth,
    monthlyModuleStatus,
    recentActivity,
  ]);

  const fetchActionRequired = useCallback(async () => {
    setActionRequiredLoading(true);
    setActionRequiredError(null);
    try {
      const items = await getBooksHealthActionRequiredUseCase.execute();
      setActionRequiredItems(items);
      setData((prev) => {
        if (!prev) return prev;
        return { ...prev, actionRequiredItems: items };
      });
    } catch (err) {
      setActionRequiredError(
        err instanceof Error ? err.message : "Failed to load action required",
      );
    } finally {
      setActionRequiredLoading(false);
    }
  }, []);

  const fetchMonthByMonth = useCallback(async () => {
    setMonthByMonthLoading(true);
    setMonthByMonthError(null);
    try {
      const months = await getBooksHealthMonthByMonthUseCase.execute(12);
      setMonthByMonth(months);
      setData((prev) => {
        if (!prev) return prev;
        return { ...prev, monthByMonthStatus: months };
      });
    } catch (err) {
      setMonthByMonthError(
        err instanceof Error ? err.message : "Failed to load month-by-month",
      );
    } finally {
      setMonthByMonthLoading(false);
    }
  }, []);

  const fetchMonthlyModuleStatus = useCallback(async () => {
    setMonthlyModuleStatusLoading(true);
    setMonthlyModuleStatusError(null);
    try {
      const mods = await getBooksHealthMonthlyModuleStatusUseCase.execute();
      setMonthlyModuleStatus(mods);
      setData((prev) => {
        if (!prev) return prev;
        return { ...prev, monthlyModuleStatus: mods };
      });
    } catch (err) {
      setMonthlyModuleStatusError(
        err instanceof Error
          ? err.message
          : "Failed to load monthly books status",
      );
    } finally {
      setMonthlyModuleStatusLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchOverview();
  }, [fetchOverview]);

  useEffect(() => {
    fetchActionRequired();
  }, [fetchActionRequired]);

  useEffect(() => {
    fetchMonthByMonth();
  }, [fetchMonthByMonth]);

  const fetchRecentActivity = useCallback(async () => {
    setRecentActivityLoading(true);
    setRecentActivityError(null);
    try {
      const items = await getBooksHealthRecentActivityUseCase.execute();
      setRecentActivity(items);
      setData((prev) => {
        if (!prev) return prev;
        return { ...prev, recentActivity: items };
      });
    } catch (err) {
      setRecentActivityError(
        err instanceof Error ? err.message : "Failed to load recent activity",
      );
    } finally {
      setRecentActivityLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchMonthlyModuleStatus();
  }, [fetchMonthlyModuleStatus]);

  useEffect(() => {
    fetchRecentActivity();
  }, [fetchRecentActivity]);

  const fetchAccountantSummary = useCallback(async () => {
    setAccountantSummaryLoading(true);
    setAccountantSummaryError(null);
    try {
      const summaryParams =
        summaryPeriod === "last_3_months"
          ? { period: "last_3_months" as const }
          : { year: selectedYear, month: selectedMonth };
      const summary =
        await getAccountantStatusSummaryUseCase.execute(summaryParams);
      setAccountantSummary(summary);
    } catch (err) {
      setAccountantSummaryError(
        err instanceof Error
          ? err.message
          : "Failed to load accountant summary cards",
      );
      setAccountantSummary(null);
    } finally {
      setAccountantSummaryLoading(false);
    }
  }, [selectedYear, selectedMonth, summaryPeriod]);

  const fetchWorkHistory = useCallback(async () => {
    setWorkHistoryLoading(true);
    setWorkHistoryError(null);
    try {
      const history = await getAccountantStatusWorkHistoryUseCase.execute();
      setWorkHistory(history);
    } catch (err) {
      setWorkHistoryError(
        err instanceof Error ? err.message : "Failed to load work history",
      );
      setWorkHistory([]);
    } finally {
      setWorkHistoryLoading(false);
    }
  }, []);

  const fetchMonthDrilldownDetails = useCallback(async () => {
    setBillsDetailLoading(true);
    setInvoicesDetailLoading(true);
    setTransactionsDetailLoading(true);
    setBillsDetailError(null);
    setInvoicesDetailError(null);
    setTransactionsDetailError(null);

    const [billsRes, invoicesRes, transactionsRes] = await Promise.allSettled([
      getAccountantStatusMonthDetailBillsUseCase.execute(
        detailPeriod === "last_3_months"
          ? {
              period: "last_3_months",
              limit: BILLS_LIMIT,
              offset: billsOffset,
            }
          : {
              year: detailYear,
              month: detailMonth,
              limit: BILLS_LIMIT,
              offset: billsOffset,
            },
      ),
      getAccountantStatusMonthDetailInvoicesUseCase.execute(
        detailPeriod === "last_3_months"
          ? {
              period: "last_3_months",
              limit: INVOICES_LIMIT,
              offset: invoicesOffset,
            }
          : {
              year: detailYear,
              month: detailMonth,
              limit: INVOICES_LIMIT,
              offset: invoicesOffset,
            },
      ),
      getAccountantStatusMonthDetailTransactionsUseCase.execute(
        detailPeriod === "last_3_months"
          ? { period: "last_3_months" }
          : { year: detailYear, month: detailMonth },
      ),
    ]);

    if (billsRes.status === "fulfilled") {
      setBillsDetail(billsRes.value);
    } else {
      setBillsDetailError("Failed to load bills details");
      setBillsDetail(null);
    }
    if (invoicesRes.status === "fulfilled") {
      setInvoicesDetail(invoicesRes.value);
    } else {
      setInvoicesDetailError("Failed to load invoices details");
      setInvoicesDetail(null);
    }
    if (transactionsRes.status === "fulfilled") {
      setTransactionsDetail(transactionsRes.value);
    } else {
      setTransactionsDetailError("Failed to load bank transactions details");
      setTransactionsDetail(null);
    }
    setBillsDetailLoading(false);
    setInvoicesDetailLoading(false);
    setTransactionsDetailLoading(false);
  }, [detailYear, detailMonth, billsOffset, invoicesOffset, detailPeriod]);

  const fetchAccountantPerformanceInsights = useCallback(async () => {
    setAccountantPerformanceInsightsLoading(true);
    setAccountantPerformanceInsightsError(null);
    try {
      const insights = await getAccountantPerformanceInsightsUseCase.execute();
      setAccountantPerformanceInsights(insights);
    } catch (err) {
      setAccountantPerformanceInsightsError(
        err instanceof Error
          ? err.message
          : "Failed to load accountant performance insights",
      );
      setAccountantPerformanceInsights(null);
    } finally {
      setAccountantPerformanceInsightsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAccountantSummary();
  }, [fetchAccountantSummary]);

  useEffect(() => {
    fetchWorkHistory();
  }, [fetchWorkHistory]);

  useEffect(() => {
    fetchMonthDrilldownDetails();
  }, [fetchMonthDrilldownDetails]);

  useEffect(() => {
    fetchAccountantPerformanceInsights();
  }, [fetchAccountantPerformanceInsights]);

  useEffect(() => {
    if (billsOffset !== 0 || invoicesOffset !== 0) {
      setBillsOffset(0);
      setInvoicesOffset(0);
    }
  }, [detailMonth, detailYear]);

  const handleSummaryMonthChange = useCallback(
    (year: number, month: number) => {
      setSelectedYear(year);
      setSelectedMonth(month);
    },
    [],
  );

  const handleSummaryPeriodChange = useCallback((period: AccountantPeriod) => {
    setSummaryPeriod(period);
  }, []);

  const handleDetailMonthChange = useCallback((year: number, month: number) => {
    setDetailYear(year);
    setDetailMonth(month);
  }, []);

  const handleDetailPeriodChange = useCallback((period: AccountantPeriod) => {
    setDetailPeriod(period);
  }, []);

  return {
    data,
    loading,
    error,
    actionRequiredLoading,
    actionRequiredError,
    monthByMonthLoading,
    monthByMonthError,
    monthlyModuleStatusLoading,
    monthlyModuleStatusError,
    recentActivity,
    recentActivityLoading,
    recentActivityError,
    refetch: fetchOverview,
    accountantSummary,
    accountantSummaryLoading,
    accountantSummaryError,
    selectedYear,
    selectedMonth,
    summaryPeriod,
    onSummaryMonthChange: handleSummaryMonthChange,
    onSummaryPeriodChange: handleSummaryPeriodChange,
    detailYear,
    detailMonth,
    detailPeriod,
    onDetailMonthChange: handleDetailMonthChange,
    onDetailPeriodChange: handleDetailPeriodChange,
    billsDetail,
    billsDetailLoading,
    billsDetailError,
    billsOffset,
    setBillsOffset,
    billsLimit: BILLS_LIMIT,
    invoicesDetail,
    invoicesDetailLoading,
    invoicesDetailError,
    invoicesOffset,
    setInvoicesOffset,
    invoicesLimit: INVOICES_LIMIT,
    transactionsDetail,
    transactionsDetailLoading,
    transactionsDetailError,
    workHistory,
    workHistoryLoading,
    workHistoryError,
    accountantPerformanceInsights,
    accountantPerformanceInsightsLoading,
    accountantPerformanceInsightsError,
    getMonthNumberFromAbbreviation,
  };
}
